import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import customerService from '../../services/customerService';
import clientCategoryService from '../../services/clientCategoryService';
import CustomerDetailModal from './CustomerDetailModal';
import { IcPlus, IcFilters, IcSearch, IcGift, IcRefresh, IcClose, IcChevronLeft, IcChevronRight, IcUsers, IcWallet } from '../../theme/icons';
import { CardSkeleton } from '../../components/Skeleton';

const PAGE_SIZE = 50;

// Tris du panneau Filtres (pilules, design Réservations › Produits).
const SORT_PILLS = [
  { key: 'score', label: 'Top score' },
  { key: 'points', label: 'Plus de points' },
  { key: 'credit', label: 'Reste à payer' },
];

const CustomerList = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Aggregates over the WHOLE filtered set (server-side), so the stat cards
  // don't change when you search / sort / paginate.
  const [totals, setTotals] = useState(null);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Filtre « crédit ancien » : quand rempli, la carte Crédit total dû (et la
  // liste) se limitent aux clients avec ventes impayées jusqu'à cette date.
  const [creditBefore, setCreditBefore] = useState('');
  const [reloadTick, setReloadTick] = useState(0);
  // Panneau « Filtres » (owner Studio) : crédit ancien + catégorie client.
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');

  const [categories, setCategories] = useState([]);

  // Selected client for the detail popup (null = closed).
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  // Sort mode. Default 'alpha' = alphabetical by name, ignoring any leading
  // number ("12 Ahmed" sorts under A). Stat cards can switch to 'points'/'credit'.
  const [sortMode, setSortMode] = useState('alpha');

  // Debounce search — refetch 300ms after typing stops.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 when search or sort changes.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, sortMode, creditBefore, selectedCategory]);

  // Fetch one page of customers from the server.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const { results, count, totals: serverTotals } = await customerService.getCustomersPage({
          page,
          pageSize: PAGE_SIZE,
          search: searchTerm,
          ordering: sortMode,
          creditBefore,
          category: selectedCategory,
        });
        if (cancelled) return;
        setCustomers(results);
        setTotalCount(count);
        setTotals(serverTotals);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching customers:', err);
        setError('Failed to load customers. Please try again later.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [page, searchTerm, sortMode, creditBefore, selectedCategory, reloadTick]);

  // Category lookup (one-shot).
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await clientCategoryService.getAllCategories();
        const arr = data.results ? data.results : Array.isArray(data) ? data : [];
        setCategories(arr);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalCount);

  // Actualiser also returns to the default (alphabetical) sort.
  const refresh = () => {
    setSortMode('alpha');
    setReloadTick((n) => n + 1);
  };

  // Clicking an active stat card returns to the default (alphabetical) sort.
  const toggleSort = (mode) => setSortMode((cur) => (cur === mode ? 'alpha' : mode));

  const handleDeleteCustomer = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) return;
    try {
      await customerService.deleteCustomer(id);
      const { results, count, totals: serverTotals } = await customerService.getCustomersPage({
        page,
        pageSize: PAGE_SIZE,
        search: searchTerm,
        ordering: sortMode,
      });
      setCustomers(results);
      setTotalCount(count);
      setTotals(serverTotals);
      if (results.length === 0 && page > 1) setPage(page - 1);
      setSelectedCustomerId(null); // close the popup after a successful delete
    } catch (err) {
      console.error('Error deleting customer:', err);
      alert('Failed to delete customer. Please try again.');
    }
  };

  const handleEditCustomer = (id) => navigate(`/clients/${id}/edit`);

  // Compact page buttons: 1 … p-1 p p+1 … N
  const pageButtons = useMemo(() => {
    const pages = new Set([1, totalPages, page, page - 1, page + 1]);
    return [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  }, [page, totalPages]);

  // Summary over the WHOLE filtered set (from the server `totals`), so these
  // stat cards stay stable across search / sort / pagination. Fall back to a
  // page-local sum only if the server didn't send totals (older API).
  const creditCount = totals
    ? Number(totals.credit_count) || 0
    : customers.filter((c) => (Number(c.credit) || 0) > 0).length;
  const creditTotal = totals
    ? Number(totals.credit_total) || 0
    : customers.reduce((s, c) => s + (Number(c.credit) || 0), 0);
  const pointsTotal = totals
    ? Number(totals.points_total) || 0
    : customers.reduce((s, c) => s + (Number(c.points) || 0), 0);

  return (
    <div className="w-full px-4 pt-2 pb-4">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-8 sm:flex-row sm:items-center">
        {/* Page name as a gray chip — same design as AddProduct/AddCustomer
            (owner Studio); icon box, title, Nouveau client removed. */}
        <div className="flex items-center min-w-0 flex-1">
          {/* Chip = refresh button (owner Studio 2026-08-13; pages « ajouter » excluded) */}
          <button type="button" onClick={() => window.location.reload()} title="Actualiser la page" className="btn-s px-4 py-2 rounded-xl text-sm">
            <IcUsers size={13} /> Gestion des clients
          </button>
        </div>
        {/* Lien « Catégories » retiré (owner Studio 2026-08-16) — déplacé vers
            Paramètres > Catégories clients. */}
      </div>

      {/* Toolbar: search + Filtres — same layout as the seller-sales/products
          pages (owner Studio): full-width search (white border, dark focus
          ring), Filtres button at the right end of the line. */}
      <div className="flex gap-3 mb-3">
        <div className="relative flex-1">
          <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            className="inp h-12 w-full pl-11 pr-10"
            placeholder="Rechercher par nom ou n° carte..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setSearchInput('')}
            >
              <IcClose />
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className="btn-s relative h-12 w-[8.5rem] px-4 py-2.5 rounded-xl"
        >
          <IcFilters className="text-sm" /> Filtres
        </button>
      </div>

      {/* Panneau Filtres — TOUT en boutons-pilules sur UNE seule rangée, même
          cadre que le panneau « Filtres » de Réservations › Produits (owner
          2026-09-09, « same frame ») : catégorie client (Toutes + une pilule
          par catégorie) puis les tris Top score / Plus de points / Reste à
          payer (re-clic = retour au tri alphabétique). Pilule active gray-900 /
          blanc, inactive gray-100 / gray-700. */}
      {showFilters && (
        <div className="flt-card rounded-2xl p-4 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {[{ id: '', name: 'Toutes' }, ...categories].map((c) => {
              const val = c.id === '' ? '' : c.id.toString();
              const active = selectedCategory === val;
              return (
                <button
                  key={val || 'all'}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setSelectedCategory(val)}
                  className={`rounded-full px-3 py-1.5 text-sm transition-colors ${active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {c.name}
                </button>
              );
            })}
            {SORT_PILLS.map((s) => {
              const active = sortMode === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleSort(s.key)}
                  className={`rounded-full px-3 py-1.5 text-sm transition-colors ${active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4" role="alert">
          {error}
        </div>
      )}

      {/* Cartes KPI (Composants-2 §5) — informatives seulement (owner 2026-09-08 :
          plus de clic = tri, les tris vivent dans le panneau Filtres). 3 cartes
          blanches, valeur points green-900, valeur crédit red-700. */}
      {customers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="kpi rounded-2xl p-5 text-left">
            <IcUsers size={16} className="kpi-icon" />
            <p className="kpi-label text-sm uppercase pr-6">Total clients</p>
            <p className="kpi-value text-2xl">{totalCount}</p>
            <p className="kpi-sub text-xs mt-1">Clients enregistrés</p>
          </div>

          <div className="kpi rounded-2xl p-5 text-left">
            <IcGift size={16} className="kpi-icon" />
            <p className="kpi-label text-sm uppercase pr-6">Points cumulés</p>
            <p className="kpi-value text-2xl text-green-900">{pointsTotal.toFixed(2)}</p>
            <p className="kpi-sub text-xs mt-1 text-gray-500">Points fidélité de tous les clients</p>
          </div>

          <div className="kpi rounded-2xl p-5 text-left">
            <IcWallet size={16} className="kpi-icon" />
            <p className="kpi-label text-sm uppercase pr-6">Crédit total dû</p>
            <p className="kpi-value text-2xl text-red-700">{creditTotal.toFixed(3)} DT</p>
            <p className="kpi-sub text-xs mt-1 truncate text-gray-500">
              {creditBefore
                ? `${creditCount} client(s) · impayés jusqu'au ${creditBefore}`
                : `${creditCount} client(s) avec crédit`}
            </p>
          </div>
        </div>
      )}

      {loading && customers.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <CardSkeleton />
        </div>
      ) : customers.length === 0 ? (
        /* État vide (Composants-2 §9) : cadre pointillé, icône 22px, phrase
           13px 600, sous-phrase 11.5px, action encre 36px. */
        <div className="empty-ki rounded-2xl p-12">
          <IcUsers size={48} className="empty-ki-icon w-12 h-12 mb-4" />
          <p className="empty-ki-title text-lg mt-0">
            {searchTerm ? 'Aucun client correspondant à la recherche' : 'Aucun client trouvé'}
          </p>
          <p className="empty-ki-sub text-sm mt-1 mb-0 max-w-md mx-auto">
            {searchTerm
              ? 'Essayez de modifier vos critères de recherche.'
              : "Pour commencer, ajoutez votre premier client en cliquant sur 'Ajouter un client'."}
          </p>
          {!searchTerm && (
            <Link
              to="/clients/add"
              className="btn-add-block w-auto mt-5 px-4 py-2.5 text-sm transition-colors"
            >
              <IcPlus className="mr-2 h-4 w-4" />
              Ajouter un client
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Cards grid (same layout as « Liste des factures ») — fixed inner scroll */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[calc(100vh-380px)] overflow-y-auto pr-1">
            {customers.map((customer) => {
              const displayName =
                customer.full_name ||
                `${customer.first_name || ''} ${customer.last_name || ''}`.trim() ||
                'Client sans nom';
              const credit = Number(customer.credit) || 0;
              return (
                <div
                  key={customer.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedCustomerId(customer.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedCustomerId(customer.id); } }}
                  className="bg-white rounded-2xl border border-gray-200 p-5 flex flex-col hover:border-gray-300 transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900"
                >
                  {/* Carte client (Composants-2 §8) : nom 600 15px + téléphone
                      12px tabulaire, badge fidélité en haut à droite. */}
                  <div className="flex items-start justify-between gap-2 mb-4">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 leading-tight truncate">{displayName}</p>
                      <p className="text-xs text-gray-400 tabular-nums truncate">{customer.phone || customer.email || '—'}</p>
                    </div>
                    {/* Livre §05 carte client : « FIDÈLE GOLD » 10.5px, 3/9 — sans médaille (owner Studio 2026-09-08) */}
                    {customer.category_name && (
                      <span className={`bdg shrink-0 ${
                        (customer.category_name || '').toLowerCase().includes('gold')
                          ? 'bdg-gold text-xs'
                          : 'bdg-fidele text-xs py-[3px]'
                      }`}>
                        {customer.category_name}
                      </span>
                    )}
                  </div>

                  {/* 3 tuiles ACHATS / SCORE / POINTS (Brand Kit §05) : bordées,
                      rayon 6, padding 7, libellé 9.5px espacé gray-400, valeur
                      700 tabulaire (taille héritée = 16px) */}
                  <div className="flex gap-2 mb-4">
                    <div className={`flex-1 border border-gray-200 rounded-lg p-2 text-center ${sortMode === 'sales' ? 'bg-gray-100' : ''}`}>
                      <p className="text-[9.5px] uppercase tracking-[0.8px] text-gray-400">Achats</p>
                      <p className="text-sm font-bold text-gray-900 tabular-nums">{customer.total_sales || 0}</p>
                    </div>
                    <div className="flex-1 border border-gray-200 rounded-lg p-2 text-center">
                      <p className="text-[9.5px] uppercase tracking-[0.8px] text-gray-400">Score</p>
                      <p className="text-sm font-bold text-gray-900 tabular-nums">{customer.score || 0}</p>
                    </div>
                    <div className="flex-1 border border-gray-200 rounded-lg p-2 text-center">
                      <p className="text-[9.5px] uppercase tracking-[0.8px] text-gray-400">Points</p>
                      <p className="text-sm font-bold text-gray-900 tabular-nums">{(Number(customer.points) || 0).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Bandeau reste à payer : corail (#FEE2E2) si crédit, avec
                      « · Max : N DT » dans le montant ; vert (#DCFCE7) si 0. */}
                  <div className={`flex items-center justify-between mb-1 rounded-lg px-3 py-2 text-[12.5px] ${credit > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                    <span className={credit > 0 ? 'text-red-800' : 'text-green-700'}>Reste à payer</span>
                    {credit > 0 ? (
                      <b className="text-red-600 tabular-nums">
                        {credit.toFixed(3)} DT{customer.credit_maximum > 0 ? ` · Max : ${Number(customer.credit_maximum).toFixed(3)} DT` : ''}
                      </b>
                    ) : (
                      <b className="text-green-700 tabular-nums">0.000 DT</b>
                    )}
                  </div>

                  {/* « Voir › » à droite — toute la carte est cliquable (owner 2026-09-08 :
                      l'ancien stopPropagation rendait la ligne du bas inerte). */}
                  <div className="flex items-center mt-auto pt-3">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSelectedCustomerId(customer.id); }}
                      className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-iface-600 hover:text-gray-900"
                    >
                      Voir <IcChevronRight className="text-xs" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination footer */}
          {/* Composants-2 §4 : compte total à gauche (12.5px gris tabulaire,
              flex-1), pages en carrés 34px, dernière page largeur auto. */}
          <div className="flex flex-col sm:flex-row items-center mt-6 px-1 gap-3">
            <div className="text-[12.5px] text-gray-500 tabular-nums flex-1 w-full sm:w-auto">
              {totalCount === 0 ? '0 client' : `${rangeStart}–${rangeEnd} sur ${totalCount} clients`}
            </div>
            <div className="inline-flex items-center gap-1">
              <button
                className="btn-day p-2"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                aria-label="Page précédente"
              >
                <IcChevronLeft className="w-3 h-3" />
              </button>
              {pageButtons.map((p, idx) => {
                const prev = pageButtons[idx - 1];
                const gap = prev && p - prev > 1;
                const wide = p === totalPages && p > 1;
                return (
                  <React.Fragment key={p}>
                    {gap && <span className="pg-dots w-auto h-auto px-2">…</span>}
                    <button
                      className={`${p === page ? 'pg-on' : 'pg-btn'}${wide ? ' w-auto px-2' : ''}`}
                      onClick={() => setPage(p)}
                      disabled={loading}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}
              <button
                className="btn-day p-2"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                aria-label="Page suivante"
              >
                <IcChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </>
      )}

      {selectedCustomerId != null && (
        <CustomerDetailModal
          customerId={selectedCustomerId}
          onClose={() => setSelectedCustomerId(null)}
          onEdit={handleEditCustomer}
          onDelete={handleDeleteCustomer}
        />
      )}
    </div>
  );
};

export default CustomerList;
